import React, { useState } from "react"

import { Button } from "../../components/shared/Buttons"
import { Flyout } from "../../components/shared/Layout"
import { AnimatedModal } from "../../components/shared/Modal"
import Typography from "../../components/shared/Typography"
import { useToast } from "../../contexts/ToastContext"

import BookableEventTypeForm from "./BookableEventTypeForm"
import { useBookableEventTypes } from "./BookableEventTypesContext"

const BookableEventTypeFlyout = ({ practice }) => {
  const {
    showFlyout,
    setShowFlyout,
    bookableEventType,
    setBookableEventType,
    saveEventType,
    updatePhoto,
    refetchEventTypes,
    deleteBookableEventType,
    removeBookableEventTypeFromList,
    updateBookableEventTypes
  } = useBookableEventTypes()

  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false)
  const [savedEventType, setSavedEventType] = useState(null) // Will be used in the schedule modal
  const [fetching, setFetching] = useState(false)
  const { showToast } = useToast()

  const editingExistingEventType = !!bookableEventType.id

  const saveEnabled = !fetching && bookableEventType.name?.length > 0 && bookableEventType.location?.length > 0

  const handleDelete = async () => {
    setFetching(true)
    try {
      const result = await deleteBookableEventType({ id: bookableEventType.id })

      if (result?.data?.deleteBookableEventType?.result === "success") {
        showToast(`Event type '${bookableEventType.name}' was deleted successfully`)
        removeBookableEventTypeFromList(bookableEventType.id)
        setDeleteModalVisible(false)
        setShowFlyout(false)
        if (refetchEventTypes) {
          refetchEventTypes()
        }
      } else {
        let errorMessage = "There was an error deleting your event type"
        if (result?.data?.deleteBookableEventType?.errors) {
          errorMessage += `: ${result.data.deleteBookableEventType.errors}`
        }
        showToast({ type: "error", content: errorMessage })
      }
    } catch (error) {
      console.error("Error deleting event type:", error) // eslint-disable-line no-console
      showToast({ type: "error", content: "There was an error deleting your event type" })
    } finally {
      setFetching(false)
    }
  }

  const navigateToSchedule = () => {
    if (savedEventType?.id) {
      window.location.href = `/portal/calendar?bookableEventTypeId=${savedEventType.id}#create-group-event`
    }
    setScheduleModalVisible(false)
  }

  const handleSave = async () => {
    setFetching(true)

    // 1. Destructure to separate the staged photo from the rest of the data
    const { stagedPhotoHandle, ...eventTypeData } = bookableEventType

    try {
      // 2. Save only the core event type data first
      const result = await saveEventType({ variables: { input: eventTypeData } })

      if (result?.data?.upsertBookableEventType?.result === "success") {
        const savedEventData = result.data.upsertBookableEventType.bookableEventType
        let finalEventType = savedEventData

        // 3. If a photo was staged, upload it now
        if (stagedPhotoHandle) {
          const photoResult = await updatePhoto({
            handle: stagedPhotoHandle,
            id: savedEventData.id
          })
          if (photoResult?.data?.updateBookableEventTypePhoto?.result === "success") {
            finalEventType = {
              ...savedEventData,
              photoUrl: photoResult.data.updateBookableEventTypePhoto.photoUrl
            }
          }
        }

        // 4. Update the state with the final, complete event type
        setBookableEventType(finalEventType)
        updateBookableEventTypes(finalEventType)

        showToast(`Event type '${finalEventType.name}' was saved successfully`)

        if (!editingExistingEventType) {
          setSavedEventType(finalEventType)
          setScheduleModalVisible(true)
        } else {
          setShowFlyout(false)
        }
      } else {
        let errorMessage = "Error"
        if (result?.data?.upsertBookableEventType?.errors) {
          errorMessage += `: ${result.data.upsertBookableEventType.errors}`
        }
        showToast({ type: "error", content: errorMessage })
      }
    } catch (error) {
      console.error("Error saving event type:", error) // eslint-disable-line no-console
      showToast({ type: "error", content: "There was an error saving your event type" })
    } finally {
      setFetching(false)
    }
  }

  return (
    <>
      <Flyout
        header={editingExistingEventType ? "Edit Group Event" : "Create Group Event"}
        visible={showFlyout}
        closeFlyout={() => setShowFlyout(false)}
        onSave={handleSave}
        saveEnabled={saveEnabled}
        footer={
          <div className={`flex w-full items-center ${editingExistingEventType ? "justify-between" : "justify-end"}`}>
            {editingExistingEventType && (
              <Button type="destructive" onClick={() => setDeleteModalVisible(true)} disabled={fetching}>
                Delete
              </Button>
            )}
            <div className="flex gap-4">
              <Button type="tertiary" onClick={() => setShowFlyout(false)} disabled={fetching}>
                Cancel
              </Button>
              <Button type="primary" onClick={handleSave} disabled={!saveEnabled || fetching}>
                Save
              </Button>
            </div>
          </div>
        }>
        <BookableEventTypeForm practice={practice} />
      </Flyout>

      {/* Delete Modal */}
      <AnimatedModal
        visible={deleteModalVisible}
        header="Delete Event Type"
        confirmLabel="Delete"
        confirmType="destructive"
        hideModal={() => setDeleteModalVisible(false)}
        onSave={handleDelete}
        disabled={fetching}
        showFooter={true}
        actionButtonCopy="Delete"
        cancelButtonCopy="Cancel"
        cancelButtonType="tertiary"
        actionButtonType="destructive">
        <p>
          Are you sure you want to delete the event type <strong>{bookableEventType.name}</strong>? This action cannot
          be undone.
        </p>
      </AnimatedModal>

      {/* Schedule Modal */}
      <AnimatedModal
        visible={scheduleModalVisible}
        header="Schedule this event now?"
        hideModal={() => {
          setScheduleModalVisible(false)
          setShowFlyout(false)
        }}
        showFooter={true}
        actionButtonCopy="Yes, schedule"
        cancelButtonCopy="Skip for now"
        cancelButtonType="tertiary"
        actionButtonType="primary"
        onSave={navigateToSchedule}>
        <div className="py-4">
          <Typography variant="title" className="text-xl">
            <span className="font-bold">{savedEventType?.name || "Group Event Name"}</span> has been created! 🎉
          </Typography>
          <Typography variant="subtitle" className="mt-2">
            Would you like to schedule this group event on your calendar now?
          </Typography>
        </div>
      </AnimatedModal>
    </>
  )
}

export default BookableEventTypeFlyout
